import { useEffect, useMemo, useRef, useState } from "react";
import { useMediaQuery } from "@/hooks";
import { buildGanttScale } from "@/shared/components";
import type { GanttMarker, GanttScale } from "@/shared/components";
import { REVISAO } from "../../../nova-obra.constants";
import { formatDataBr } from "../../../components/format-data";
import { rotulosDosMeses } from "./revisao-gantt.model";
import type { GanttSegmento } from "./revisao-gantt.model";
import type {
  BarraRevisao,
  CardEmFoco,
  DadosCard,
  LinhaRevisao,
  RevisaoGanttProps,
  TipoBarra,
} from "./types-revisao-gantt";

/** Largura da trilha no Figma (10706:406666); vale até o primeiro `ResizeObserver`. */
const TRILHA_PADRAO_PX = 579;

const CARD_LARGURA_PX = 360;

const CARD_MARGEM_PX = 8;

/** Abaixo disto o "Nd" não cabe dentro da barra e vai para a direita (Figma: 2d = 18 px fora, 3d = 28 px dentro). */
const BARRA_MIN_ROTULO_PX = 24;

/** Mobile (Figma 12322:536962): a tabela rola na horizontal e a trilha mantém a largura do desktop. */
const MOBILE_QUERY = "(max-width: 768px)";

export function useRevisaoGantt({ modelo }: RevisaoGanttProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [larguraTrilha, setLarguraTrilha] = useState(TRILHA_PADRAO_PX);
  const [card, setCard] = useState<CardEmFoco | null>(null);
  const mobile = useMediaQuery(MOBILE_QUERY);

  useEffect(() => {
    // A trilha é o 2º cabeçalho de coluna da tabela da base (o 1º é "Período").
    const trilha = containerRef.current?.querySelectorAll<HTMLElement>('[role="columnheader"]')[1];
    if (!trilha || typeof ResizeObserver === "undefined") return undefined;
    const observador = new ResizeObserver(([entrada]) => {
      if (entrada && entrada.contentRect.width > 0) setLarguraTrilha(entrada.contentRect.width);
    });
    observador.observe(trilha);
    return () => observador.disconnect();
  }, []);

  const focar = (dados: DadosCard, alvo: HTMLElement) => {
    const caixa = alvo.getBoundingClientRect();
    const esquerda = Math.min(
      Math.max(caixa.left + caixa.width / 2 - CARD_LARGURA_PX / 2, CARD_MARGEM_PX),
      Math.max(window.innerWidth - CARD_LARGURA_PX - CARD_MARGEM_PX, CARD_MARGEM_PX),
    );
    // Sem espaço acima da barra, o card abre abaixo dela.
    const acima = caixa.top > 140;
    setCard({
      dados,
      estilo: acima
        ? { left: esquerda, top: caixa.top - CARD_MARGEM_PX, transform: "translateY(-100%)" }
        : { left: esquerda, top: caixa.bottom + CARD_MARGEM_PX },
    });
  };
  const sair = () => setCard(null);

  // Os meses do eixo com o rótulo que cabe na largura de cada um ("Nov 2026", "Nov" ou nada).
  const escala = useMemo<GanttScale>(() => {
    const rotulos = rotulosDosMeses(modelo.meses, larguraTrilha);
    const base = buildGanttScale({ start: modelo.eixo.inicio, end: modelo.eixo.fim });
    return {
      ...base,
      months: base.months.map((mes, indice) => ({ ...mes, longLabel: rotulos[indice] ?? "" })),
    };
  }, [modelo.eixo, modelo.meses, larguraTrilha]);

  const linhas = useMemo<LinhaRevisao[]>(() => {
    const barra = (
      tipo: TipoBarra,
      segmento: Pick<GanttSegmento, "esquerda" | "largura" | "dias">,
      dados: DadosCard,
      testId: string,
    ): BarraRevisao => {
      const rotulo = `${segmento.dias}d`;
      return {
        tipo,
        inicio: segmento.esquerda,
        fim: segmento.esquerda + segmento.largura,
        rotulo,
        dentro: (segmento.largura / 100) * larguraTrilha >= BARRA_MIN_ROTULO_PX,
        dados,
        ariaLabel: `${dados.titulo}: ${rotulo}`,
        testId,
      };
    };
    const dadosDe = (titulo: string, tipo: TipoBarra, segmento: GanttSegmento): DadosCard => ({
      titulo,
      tipo,
      inicio: segmento.inicio,
      fim: segmento.fim,
      dias: segmento.dias,
      meta: null,
    });

    return [
      {
        tipo: "barra",
        key: "total",
        rotulo: REVISAO.duracao.ganttTotal,
        destaque: true,
        barra: barra(
          "total",
          modelo.total,
          dadosDe(REVISAO.duracao.ganttTotal, "total", modelo.total),
          "revisao-gantt-total",
        ),
      },
      ...(modelo.obra
        ? [
            {
              tipo: "barra" as const,
              key: "obra",
              rotulo: REVISAO.duracao.ganttObra,
              destaque: undefined,
              barra: barra(
                "obra",
                modelo.obra,
                dadosDe(REVISAO.duracao.ganttObra, "obra", modelo.obra),
                "revisao-gantt-obra",
              ),
            },
          ]
        : []),
      ...modelo.periodos.map((periodo) => ({
        tipo: "barra" as const,
        key: periodo.uid,
        rotulo: periodo.rotulo,
        destaque: undefined,
        barra: barra(
          periodo.categoria,
          periodo,
          {
            titulo: periodo.rotulo,
            tipo: periodo.categoria,
            inicio: periodo.inicio,
            fim: periodo.fim,
            dias: periodo.dias,
            meta: periodo.meta,
          },
          "revisao-gantt-barra",
        ),
      })),
      ...modelo.limites.map((limite) => ({
        tipo: "limite" as const,
        key: `limite-${limite.nome}-${limite.data}`,
        rotulo: REVISAO.duracao.ganttLimite(limite.nome),
        destaque: undefined,
        data: formatDataBr(limite.data),
        estiloData: { left: `calc(${limite.esquerda}% + var(--ds-spacing-1-5))` },
      })),
    ];
  }, [modelo, larguraTrilha]);

  // O início (estrela) fica na linha do Total; cada limite desce até a última linha com a mesma data (`ultimaLinha`).
  const marcos = useMemo<GanttMarker[]>(
    () => [
      {
        key: "inicio",
        kind: "start",
        date: modelo.total.inicio,
        row: "total",
        testId: "revisao-gantt-inicio",
      },
      ...modelo.limites.map((limite): GanttMarker => ({
        key: `marco-${limite.nome}-${limite.data}`,
        kind: "limit",
        date: limite.data,
        untilRow: linhas[limite.ultimaLinha]?.key,
        testId: "revisao-gantt-limite-linha",
      })),
    ],
    [modelo.total.inicio, modelo.limites, linhas],
  );

  return {
    containerRef,
    card,
    focar,
    sair,
    escala,
    linhas,
    marcos,
    larguraColuna: mobile ? "var(--ds-size-gantt-label-mobile)" : "var(--ds-size-gantt-label)",
    // No desktop a trilha ocupa o que sobra; no mobile ela tem a largura do desktop, dividida entre os meses.
    larguraMes: mobile
      ? `calc(var(--ds-size-gantt-track-mobile) / ${escala.months.length})`
      : "0rem",
    alturaLinha: "var(--ds-size-control-40)",
  };
}
